// "Fill a form": the project's forms, loaded when the task is first picked, and which one.

import { useEffect, useState } from 'react';
import { bannerAPI, type TaskFormOption } from './bannerApi';

export function useFillForm({
  on,
  projectId,
  onPick,
}: {
  on: boolean;
  projectId: number | null;
  /** The default copy names the form, so it follows the pick until edited. */
  onPick: (formName: string | null) => void;
}) {
  const [forms, setForms] = useState<TaskFormOption[] | null>(null);
  const [formsProject, setFormsProject] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [taskId, setTaskId] = useState('');

  useEffect(() => {
    if (!on || projectId == null || formsProject === projectId) return;
    if (formsProject != null) {
      // Another project's pick: drop it now, and its name from untouched copy.
      setTaskId('');
      onPick(null);
    }
    let cancelled = false;
    setError(null);
    bannerAPI
      .taskForms(projectId)
      .then((res) => {
        if (cancelled) return;
        setForms(res.data?.data ?? []);
        setFormsProject(projectId);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || 'Could not load the forms for this project.');
      });
    return () => {
      cancelled = true;
    };
    // Only a new task, project or retry reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, projectId, formsProject, attempt]);

  const loaded = formsProject === projectId && forms != null;
  const picked = loaded ? forms.find((f) => f.id === taskId) ?? null : null;

  const pick = (id: string) => {
    setTaskId(id);
    onPick(forms?.find((f) => f.id === id)?.name ?? null);
  };

  // What still stops a send, in the order the form asks for it.
  const missing = !on
    ? null
    : projectId == null
    ? 'Pick a project first.'
    : error
    ? 'The forms could not be loaded. Try again below.'
    : !loaded
    ? 'Loading the forms…'
    : !picked
    ? 'Pick a form.'
    : null;

  return {
    forms: loaded ? forms : null,
    error,
    retry: () => setAttempt((n) => n + 1),
    // A form was made or changed: read the list again, keeping what is picked.
    reload: () => {
      setFormsProject(null);
      setAttempt((n) => n + 1);
    },
    picked,
    pick,
    missing,
    settings: on && picked ? { taskId: picked.id } : null,
    // The server's own first line on a card with no button: an older app's.
    line: on ? `Open "${picked?.name ?? 'your form'}" from your Tasks list` : null,
  };
}

export type FillForm = ReturnType<typeof useFillForm>;
